import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "What makes Bespoke different from a typical commercial cleaning contractor?",
    a: "We operate as a partnership, not a vendor. Every property gets a scoped brief, a briefed crew, calibrated equipment, and a same-day photo debrief. Veteran-owned, fully insured to $2M, bonded, and locked into a documented cadence — never verbal handshakes.",
  },
  {
    q: "How fast can you start?",
    a: "Instant plans go live the day you subscribe. For custom retainers, we scope within 24 hours and mobilize within 5 business days for standard properties, next-day for emergencies (storm response, graffiti).",
  },
  {
    q: "Do you carry insurance?",
    a: "Yes — $2M general liability, workers' comp, and commercial auto. Certificates of insurance are provided within 24 hours of contract signing and re-issued annually. Additional insured status added on request at no charge.",
  },
  {
    q: "How does the Storm Express re-clean work?",
    a: "Every subscription includes a re-clean within 48–72 hours of any documented storm event (rain, snow, wind ≥ 30 mph) that visibly compromises the property. No extra charge. No arguments. Just documented, timestamped restoration.",
  },
  {
    q: "What if I'm not happy with a visit?",
    a: "Every visit closes with a photo debrief. If any zone doesn't meet standard, notify us within 24 hours and we return within 48 hours to remediate at no charge. If we can't resolve it, we credit the visit — no invoicing dispute.",
  },
  {
    q: "Do you service multi-location portfolios?",
    a: "Yes — this is our specialty. We coordinate cadence, staffing, and documentation across NYC (5 boroughs + Long Island) and North Carolina (Charlotte, Raleigh–Durham, Asheville). One point of contact, one accountable partner, one dashboard.",
  },
  {
    q: "Can I pause my subscription?",
    a: "Yes. Pause for up to 60 days per calendar year at no penalty. Cancel anytime with 30 days' notice. Founding 100 members keep their rate locked for life even if paused.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section className="py-32 md:py-40" style={{ background: "var(--bg-2)" }} data-testid="faq-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <div className="overline mb-6">Fine Print</div>
            <h2 className="font-display text-5xl md:text-6xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              Answered <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>before</span> you ask.
            </h2>
            <p className="mt-8 font-body text-base md:text-lg" style={{ color: "var(--text-2)", lineHeight: 1.6 }}>
              The most common questions from portfolio managers evaluating Bespoke as their exterior partner.
            </p>
          </div>

          <div className="md:col-span-8 space-y-3">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  className="overflow-hidden transition-colors duration-500"
                  style={{
                    background: isOpen ? "var(--surface-glass)" : "rgba(255,255,255,0.015)",
                    border: `1px solid ${isOpen ? "var(--line-gold)" : "var(--line)"}`,
                    borderRadius: "var(--r-lg)",
                    backdropFilter: "blur(20px)",
                  }}
                  data-testid={`faq-${i}`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full flex items-center justify-between gap-6 text-left px-6 md:px-8 py-6 md:py-7"
                    data-cursor="hover"
                  >
                    <span className="font-display text-xl md:text-2xl" style={{ letterSpacing: "-0.015em", color: "var(--text)", lineHeight: 1.15 }}>
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="shrink-0 w-10 h-10 flex items-center justify-center"
                      style={{
                        borderRadius: "var(--r-full)",
                        border: `1px solid ${isOpen ? "var(--gold)" : "var(--line-2)"}`,
                        color: isOpen ? "var(--gold)" : "var(--text-2)",
                      }}
                    >
                      <Plus size={16} strokeWidth={1.5} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      >
                        <div className="px-6 md:px-8 pb-7 pt-1">
                          <p className="font-body text-base md:text-lg max-w-2xl" style={{ color: "var(--text-2)", lineHeight: 1.65 }}>
                            {f.a}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
